import { useState, useRef, useCallback } from "react";

const S = {
  card:   { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  hdr:    { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"space-between" },
  btn:    (c="#b5003c") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut: { padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  label:  { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
};

// Myntra specs
const MYNTRA_SPECS = {
  width:      1080,
  height:     1440,
  maxSizeKB:  500,
  format:     "JPEG",
  background: "#FFFFFF",
};

const PLATFORM_SPECS = [
  { name:"Myntra",   w:1080, h:1440, maxKB:500,  bg:"#fff", note:"3:4 ratio, white bg, <500KB" },
  { name:"Amazon",   w:2000, h:2000, maxKB:10240, bg:"#fff", note:"Square, white bg, <10MB" },
  { name:"Flipkart", w:1000, h:1000, maxKB:5120,  bg:"#fff", note:"Square, white bg, <5MB" },
  { name:"Shopify",  w:2048, h:2048, maxKB:20480, bg:"#fff", note:"Square, any bg, <20MB" },
  { name:"Meesho",   w:1080, h:1080, maxKB:5120,  bg:"#fff", note:"Square, white bg, <5MB" },
];

function processImage(file, spec) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width  = spec.w;
      canvas.height = spec.h;
      const ctx = canvas.getContext("2d");

      // White background
      ctx.fillStyle = spec.bg || "#fff";
      ctx.fillRect(0, 0, spec.w, spec.h);

      // Scale image to fit within canvas keeping aspect ratio
      const scale     = Math.min(spec.w / img.width, spec.h / img.height);
      const newWidth  = img.width  * scale;
      const newHeight = img.height * scale;
      const x = (spec.w - newWidth)  / 2;
      const y = (spec.h - newHeight) / 2;

      ctx.drawImage(img, x, y, newWidth, newHeight);
      URL.revokeObjectURL(url);

      // Convert to JPEG and compress to fit maxKB
      let quality = 0.92;
      let dataUrl = canvas.toDataURL("image/jpeg", quality);

      // Reduce quality until under size limit
      const maxBytes = (spec.maxKB || 500) * 1024;
      while (dataUrl.length * 0.75 > maxBytes && quality > 0.3) {
        quality -= 0.05;
        dataUrl = canvas.toDataURL("image/jpeg", quality);
      }

      const sizeKB = Math.round(dataUrl.length * 0.75 / 1024);
      resolve({ dataUrl, sizeKB, quality: Math.round(quality * 100), width: spec.w, height: spec.h });
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = url;
  });
}

export default function PageMyntraImages({ toast }) {
  const [images,      setImages]      = useState([]); // { file, preview, name, results }
  const [processing,  setProcessing]  = useState(false);
  const [selPlatforms,setSelPlatforms]= useState(["Myntra"]);
  const [bgColor,     setBgColor]     = useState("#ffffff");
  const [done,        setDone]        = useState(false);
  const fileRef = useRef(null);

  const handleFiles = useCallback((files) => {
    const newImages = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .map((f) => ({
        file:    f,
        preview: URL.createObjectURL(f),
        name:    f.name.replace(/\.[^.]+$/, ""),
        size:    Math.round(f.size / 1024),
        results: null,
      }));
    if (!newImages.length) { toast("Please upload image files only", "error"); return; }
    setImages((prev) => [...prev, ...newImages]);
    setDone(false);
    toast(newImages.length + " image(s) added ✓", "success");
  }, [toast]);

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const processAll = async () => {
    if (!images.length)      { toast("Upload at least one image", "error"); return; }
    if (!selPlatforms.length){ toast("Select at least one platform", "error"); return; }
    setProcessing(true);
    setDone(false);

    const specs = PLATFORM_SPECS.filter((p) => selPlatforms.includes(p.name))
      .map((p) => ({ ...p, bg: bgColor || p.bg }));

    const updated = await Promise.all(
      images.map(async (img) => {
        const results = {};
        for (const spec of specs) {
          try {
            results[spec.name] = await processImage(img.file, spec);
          } catch(e) {
            results[spec.name] = { error: e.message };
          }
        }
        return { ...img, results };
      })
    );

    setImages(updated);
    setProcessing(false);
    setDone(true);
    toast("✅ All images processed!", "success");
  };

  const downloadOne = (result, filename, platform) => {
    const a = document.createElement("a");
    a.href     = result.dataUrl;
    a.download = `${filename}_${platform}_${result.width}x${result.height}.jpg`;
    a.click();
  };

  const downloadAll = () => {
    images.forEach((img) => {
      if (!img.results) return;
      Object.entries(img.results).forEach(([platform, result]) => {
        if (result.dataUrl) {
          setTimeout(() => downloadOne(result, img.name, platform), 100);
        }
      });
    });
    toast("Downloading all images...", "success");
  };

  const removeImage = (i) => {
    setImages((prev) => prev.filter((_, idx) => idx !== i));
    setDone(false);
  };

  const clearAll = () => { setImages([]); setDone(false); };

  const togglePlatform = (name) => {
    setSelPlatforms((prev) =>
      prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name]
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>🖼️ Image Formatter</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Resize & convert product images to platform specifications</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {done && images.length > 0 && (
            <button style={S.btn()} onClick={downloadAll}>⬇ Download All</button>
          )}
          {images.length > 0 && (
            <button style={{ ...S.btnOut, color:"#dc2626", borderColor:"#fecaca" }} onClick={clearAll}>Clear All</button>
          )}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"260px 1fr", gap:14, alignItems:"start" }}>

        {/* Left — Settings */}
        <div>
          {/* Platform selector */}
          <div style={S.card}>
            <div style={S.hdr}><span>🌐 Target Platforms</span></div>
            <div style={{ padding:14 }}>
              {PLATFORM_SPECS.map((pl) => {
                const sel = selPlatforms.includes(pl.name);
                const colors = { Myntra:"#b5003c", Amazon:"#c45000", Flipkart:"#1b57c0", Shopify:"#5a8a00", Meesho:"#9f2089" };
                const c = colors[pl.name] || "#6b7280";
                return (
                  <div key={pl.name} onClick={() => togglePlatform(pl.name)}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:7, cursor:"pointer", marginBottom:6, border:"1.5px solid "+(sel?c:"#e5e7eb"), background:sel?c+"10":"#fff", transition:"all .15s" }}>
                    <div style={{ width:16, height:16, borderRadius:4, border:"2px solid "+(sel?c:"#d1d5db"), background:sel?c:"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      {sel && <span style={{ color:"#fff", fontSize:9, fontWeight:900 }}>✓</span>}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:12, color:sel?c:"#374151" }}>{pl.name}</div>
                      <div style={{ fontSize:10, color:"#9ca3af" }}>{pl.w}×{pl.h}px · {pl.note}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Background color */}
          <div style={S.card}>
            <div style={S.hdr}><span>🎨 Background</span></div>
            <div style={{ padding:14 }}>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
                {["#ffffff","#f5f5f5","#fafafa","#000000","#F47920"].map((c) => (
                  <div key={c} onClick={() => setBgColor(c)}
                    style={{ width:28, height:28, borderRadius:6, background:c, cursor:"pointer", border:"2px solid "+(bgColor===c?"#6366f1":"#d1d5db"), boxShadow:bgColor===c?"0 0 0 2px #6366f1":"none" }}/>
                ))}
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <label style={S.label}>Custom:</label>
                <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)}
                  style={{ width:40, height:32, borderRadius:6, border:"1px solid #d1d5db", cursor:"pointer", padding:2 }}/>
                <span style={{ fontSize:11, color:"#6b7280", fontFamily:"monospace" }}>{bgColor}</span>
              </div>
              <div style={{ fontSize:11, color:"#9ca3af", marginTop:8 }}>
                Most platforms require pure white (#ffffff)
              </div>
            </div>
          </div>

          {/* Process button */}
          <button style={{ ...S.btn(), width:"100%", padding:"11px", fontSize:13, opacity:(!images.length||processing)?0.6:1 }}
            onClick={processAll} disabled={!images.length || processing}>
            {processing ? "⏳ Processing…" : `🚀 Convert ${images.length} Image${images.length!==1?"s":""}`}
          </button>

          {/* Specs reference */}
          <div style={{ ...S.card, marginTop:14 }}>
            <div style={S.hdr}><span>📋 Platform Specs</span></div>
            <div style={{ padding:10 }}>
              {PLATFORM_SPECS.map((pl) => (
                <div key={pl.name} style={{ display:"flex", justifyContent:"space-between", padding:"5px 4px", borderBottom:"1px solid #f3f4f6", fontSize:11 }}>
                  <span style={{ fontWeight:600, color:"#374151" }}>{pl.name}</span>
                  <span style={{ color:"#6b7280" }}>{pl.w}×{pl.h} · ≤{pl.maxKB>=1024?pl.maxKB/1024+"MB":pl.maxKB+"KB"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right — Upload + Results */}
        <div>
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor="#b5003c"; e.currentTarget.style.background="#fff5f7"; }}
            onDragLeave={(e) => { e.currentTarget.style.borderColor="#e5e7eb"; e.currentTarget.style.background="#fafafa"; }}
            onDrop={(e) => { e.currentTarget.style.borderColor="#e5e7eb"; e.currentTarget.style.background="#fafafa"; onDrop(e); }}
            onClick={() => fileRef.current?.click()}
            style={{ border:"2px dashed #e5e7eb", borderRadius:10, padding:images.length?"16px":"40px 20px", textAlign:"center", cursor:"pointer", background:"#fafafa", marginBottom:14, transition:"all .15s" }}>
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:"none" }}
              onChange={(e) => { handleFiles(e.target.files); e.target.value=""; }}/>
            {images.length === 0 ? (
              <>
                <div style={{ fontSize:40, marginBottom:10 }}>🖼️</div>
                <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>Drop images here or click to upload</div>
                <div style={{ fontSize:12, color:"#9ca3af" }}>PNG, JPG, WEBP · Multiple files supported</div>
              </>
            ) : (
              <div style={{ fontSize:12, color:"#b5003c", fontWeight:600 }}>+ Drop more images or click to add</div>
            )}
          </div>

          {/* Image cards */}
          {images.length > 0 && (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {images.map((img, i) => (
                <div key={i} style={S.card}>
                  <div style={S.hdr}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <img src={img.preview} alt="" style={{ width:36, height:36, objectFit:"cover", borderRadius:6, border:"1px solid #e5e7eb" }}/>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{img.name}</div>
                        <div style={{ fontSize:11, color:"#9ca3af" }}>Original: {img.size} KB</div>
                      </div>
                    </div>
                    <button onClick={() => removeImage(i)}
                      style={{ background:"none", border:"none", color:"#9ca3af", cursor:"pointer", fontSize:18, padding:0 }}>×</button>
                  </div>

                  {/* Results grid */}
                  {img.results && (
                    <div style={{ padding:14, display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:10 }}>
                      {Object.entries(img.results).map(([platform, result]) => {
                        const colors = { Myntra:"#b5003c", Amazon:"#c45000", Flipkart:"#1b57c0", Shopify:"#5a8a00", Meesho:"#9f2089" };
                        const c = colors[platform] || "#6b7280";
                        return (
                          <div key={platform} style={{ border:"1.5px solid #e5e7eb", borderRadius:8, overflow:"hidden", borderTop:"3px solid "+c }}>
                            {result.error ? (
                              <div style={{ padding:12, fontSize:11, color:"#dc2626" }}>❌ {result.error}</div>
                            ) : (
                              <>
                                <img src={result.dataUrl} alt={platform}
                                  style={{ width:"100%", aspectRatio:"1/1", objectFit:"contain", background:"#f9f9f9", display:"block" }}/>
                                <div style={{ padding:"8px 10px" }}>
                                  <div style={{ fontWeight:700, fontSize:11, color:c, marginBottom:3 }}>{platform}</div>
                                  <div style={{ fontSize:10, color:"#6b7280" }}>
                                    {result.width}×{result.height}px · {result.sizeKB}KB
                                  </div>
                                  <div style={{ fontSize:10, color: result.sizeKB > (PLATFORM_SPECS.find(p=>p.name===platform)?.maxKB||500) ? "#dc2626" : "#15803d", marginBottom:6 }}>
                                    {result.sizeKB > (PLATFORM_SPECS.find(p=>p.name===platform)?.maxKB||500) ? "⚠ Over size limit" : "✓ Within limit"}
                                  </div>
                                  <button style={{ ...S.btn(c), width:"100%", fontSize:10, padding:"5px" }}
                                    onClick={() => downloadOne(result, img.name, platform)}>
                                    ⬇ Download
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!img.results && !processing && (
                    <div style={{ padding:"14px 16px", fontSize:12, color:"#9ca3af" }}>
                      Click Convert to process this image
                    </div>
                  )}

                  {!img.results && processing && (
                    <div style={{ padding:"14px 16px", fontSize:12, color:"#6366f1" }}>
                      ⏳ Processing…
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
